import { motion } from "framer-motion";
import { ArrowUpRight, Clock, MapPin, Phone, User } from "lucide-react";
import { LiveDot, LOGO_URL, PHONE_DISPLAY, PHONE_TEL } from "./Nav";

const EASE = [0.16, 1, 0.3, 1];

const MAPS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=336+Superior+Rd,+Church+Point,+LA+70583";

const INFO = [
  { icon: User, label: "Talk to", value: "Blake Granger", testid: "contact-owner" },
  {
    icon: MapPin,
    label: "The Yard",
    value: "336 Superior Rd. Church Point, LA 70583",
    testid: "contact-address",
    href: MAPS_URL,
    hint: "Tap for directions",
  },
  { icon: Clock, label: "Hours", value: "Open 24/7 — 365 days a year", testid: "contact-hours" },
];

export default function Contact() {
  return (
    <footer id="contact" className="relative border-t border-white/10 bg-machine" data-testid="contact-section">
      <div className="mx-auto w-full max-w-[1700px] px-4 py-24 sm:px-6 sm:py-32 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE }}
          className="flex items-center gap-4"
        >
          <span className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.35em] text-uv">
            <LiveDot /> Get us on the line
          </span>
          <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
        </motion.div>

        <div className="mt-14 overflow-hidden">
          <motion.a
            href={PHONE_TEL}
            data-testid="contact-call-button"
            initial={{ y: "60%", opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.1, ease: EASE }}
            className="group flex flex-wrap items-center gap-x-8 gap-y-4"
          >
            <span className="font-display text-[13vw] font-semibold uppercase leading-none tracking-tighter text-slate-50 transition-colors duration-300 group-hover:text-uv sm:text-[10vw] lg:text-[8.5vw]">
              {PHONE_DISPLAY}
            </span>
            <span className="flex h-16 w-16 items-center justify-center border border-white/15 transition-colors duration-300 group-hover:border-uv group-hover:bg-uv/10 sm:h-24 sm:w-24">
              <ArrowUpRight className="h-8 w-8 text-slate-200 transition-transform duration-300 group-hover:-translate-y-1.5 group-hover:translate-x-1.5 group-hover:text-uv sm:h-10 sm:w-10" />
            </span>
          </motion.a>
        </div>

        <div className="mt-20 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-3">
          {INFO.map(({ icon: Icon, label, value, testid, href, hint }, i) => {
            const Tag = href ? motion.a : motion.div;
            const linkProps = href
              ? { href, target: "_blank", rel: "noopener noreferrer" }
              : {};
            return (
              <Tag
                key={testid}
                {...linkProps}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.8, ease: EASE, delay: 0.08 * i }}
                className={`group bg-machine p-7 sm:p-9 ${href ? "block transition-colors duration-300 hover:bg-steel" : ""}`}
                data-testid={testid}
              >
                <div className="flex items-center justify-between">
                  <Icon className="h-5 w-5 text-uv" />
                  {href && (
                    <ArrowUpRight className="h-4 w-4 text-slate-500 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-uv" />
                  )}
                </div>
                <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">{label}</p>
                <p className="mt-2 font-display text-xl font-medium tracking-tight text-slate-100 sm:text-2xl">{value}</p>
                {hint && (
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-uv">{hint}</p>
                )}
              </Tag>
            );
          })}
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex w-full max-w-[1700px] flex-col items-start justify-between gap-6 px-4 py-8 sm:flex-row sm:items-center sm:px-6 lg:px-10">
          <span className="frame-chamfer-sm inline-block bg-white px-3 py-1.5">
            <img src={LOGO_URL} alt="Tube Tech Services INC" className="h-7 w-auto" data-testid="footer-logo" />
          </span>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500" data-testid="footer-copy">
            © 2026 Tube Tech Services INC — Church Point, Louisiana
          </p>
          <a
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-directions-link"
            className="group flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-300 transition-colors duration-300 hover:text-uv"
          >
            <MapPin className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5" />
            Directions
          </a>
          <a
            href={PHONE_TEL}
            data-testid="footer-call-link"
            className="group flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-300 transition-colors duration-300 hover:text-uv"
          >
            <Phone className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-12" />
            {PHONE_DISPLAY}
          </a>
        </div>
      </div>
    </footer>
  );
}
